"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useWindowSize = exports.useFetch = exports.useBlurryDateParse = void 0;
var _react = require("react");
require("whatwg-fetch");
/** Custom Hooks for internal use
 *   - useFetch - gets data from a URL, returns parsed JSON
 *   - useWindowSize - tracks the size of browser window
 *   - useBlurryDateParse - Creates a callback that parses an
 *     input value for a date or range of dates.
 */

/**
 * A hook that will fetch a URL, and set the parsed JSON as the data property
 * of the hook's return value.
 *
 * @param url {string}: The URL endpoint to fetch and parse as JSON.
 * @returns {{status: string, data: Object}}
 */
const useFetch = url => {
  const cache = (0, _react.useRef)({});
  const [status, setStatus] = (0, _react.useState)('idle');
  const [data, setData] = (0, _react.useState)({});
  (0, _react.useEffect)(() => {
    const fetchData = async () => {
      setStatus('fetching');
      let data = null;
      if (cache.current[url]) {
        data = cache.current[url];
      } else {
        const response = await fetch(url);
        data = await response.json();
        cache.current[url] = data;
      }
      setData(data);
      setStatus('fetched');
    };
    if (url) fetchData();
  }, [url, status, data]);
  return {
    status,
    data
  };
};

/**
 * A hook that emits the size of the window upon resizing the browser window.
 *
 * @returns {{width:Number, height:Number}} The width and height of the window.
 */
exports.useFetch = useFetch;
const useWindowSize = () => {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = (0, _react.useState)({
    height: undefined,
    width: undefined
  });
  (0, _react.useEffect)(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    }

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures that effect is only run on mount

  return windowSize;
};

/**
 * Create a callback that parses an input value for a date or range of dates.
 *
 * @param selectsRange {boolean}: Should the parser try parsing one date, or two?
 * @param inputValue {string}: The HTML input value to be parsed.
 * @param onSelect {function}: The callback to invoke with selected data.
 * @param onBlur {function}: The callback to invoke when the field is blurred.
 * @returns {function}: The event handler that will process a "blur" event.
 */
exports.useWindowSize = useWindowSize;
const useBlurryDateParse = (selectsRange, inputValue, onSelect, onBlur) => e => {
  if (!selectsRange) {
    const theDate = new Date(inputValue);
    if (isNaN(theDate.valueOf())) {
      onSelect(null, e);
    } else {
      onSelect(theDate, e);
    }
  } else {
    const parts = inputValue.split(' - ');
    const changes = [];
    if (parts.length === 1) {
      const startDate = new Date(parts[0]);
      if (isNaN(startDate.valueOf())) {
        changes.push(null);
      } else {
        changes.push(startDate);
      }
      changes.push(null);
    } else {
      const startDate = new Date(parts[0]);
      const endDate = new Date(parts[1]);
      if (isNaN(startDate.valueOf())) {
        changes.push(null);
      } else {
        changes.push(startDate);
      }
      if (isNaN(endDate.valueOf())) {
        changes.push(null);
      } else {
        changes.push(endDate);
      }
    }
    onSelect(changes, e);
  }
  onBlur();
};
exports.useBlurryDateParse = useBlurryDateParse;